// src/containers/VehicleList/VehicleDisplay/VehicleTab/MpiFormBuilder/index.js
import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import axios from 'axios';
import _ from 'lodash';

import ErrorBoundary from './ErrorBoundary';
import VehicleHeader from './components/VehicleHeader';
import SimpleHeader from './components/SimpleHeader';
import TotalsDisplay from './components/TotalsDisplay';
import SectionRenderer from './components/SectionRenderer';
import SectionRendererWide from './components/SectionRendererWide';
import DeleteModal from './components/DeleteModal';
import PresetSelector from './components/PresetSelector';
import ChecklistLegend from './components/ChecklistLegend';

import useFormInstructions from './hooks/useFormInstructions';
import useMpiItems from './hooks/useMpiItems';
import useTotals from './hooks/useTotals';

import { activeVehicleWithPromise } from '../../../../../actions/lookups';
import { getCatalogParts } from '../../../../../actions/apiGetInfo';
import VehiclePartsSection from '../../../../../components/Parts/VehiclePartsSection';
import { vehiclePartsTotal } from '../../../../../components/Parts/partsUtils';
import {
  addMpiWorkItem,
  deleteMpiWorkItem,
  updateMpiWorkItem,
  createMpiReportPdf,
  createMpiReportPdfAndSend,
} from '../../../../../actions/apiMpiWorkItems';
import { deleteAttachmentWithRefresh } from '../../../../../actions/apiAttachments';

import { toNumber } from './utils/toNumber';
import { debounce } from './utils/debounce';

const MpiFormBuilder = ({ vehicle, vehicle_id, tab }) => {
  const dispatch = useDispatch();
  const printRef = useRef();
  const tableRefs = useRef({});
  const sectionRefs = useRef({});

  const active_vehicle = vehicle || useSelector(state => state.settings_refresh?.active_vehicle || {});
  const installation = useSelector(state => state.settings_dealerships?.installation || {});
  const dealer_settings = useSelector(state => state.settings_site?.dealer_settings || []);
  const work_item_presets = useSelector(state => state.settings_refresh?.work_item_presets || {});
  const currentUser = useSelector(state => state.current_user || {});
  const currentUserProfile = useSelector(state => state.settings_users.current_user_profile || {});
  const current_user_profile_id = String(currentUser.user || currentUserProfile.id || 'default_user');
  const current_dealership = useSelector(state => state.settings_dealerships.current_dealership || {});

  const formInstructions = useFormInstructions(tab);
  const customFormId = formInstructions?.customFormId || null;
  const formSendSftp = formInstructions?.sendSftp !== undefined ? formInstructions.sendSftp : true;

  // Get latest MPI report for updated_at display
  const latestMpiReport = active_vehicle.mpi_reports?.[0] || {};
  
  // Simple native date formatting (no external dependency)
  const lastUpdated = latestMpiReport.updated_at 
    ? new Date(latestMpiReport.updated_at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    : null;

  // Thumbnail proxy
  const [proxiedThumbnailUrl, setProxiedThumbnailUrl] = useState('');
  const originalThumbnailSrc = _.find(active_vehicle?.vehicle_properties, { key: 'thumbnail' })?.value;
  const siteid = `${current_dealership}_${active_vehicle.id}`;

  useEffect(() => {
    if (!originalThumbnailSrc || originalThumbnailSrc.includes('data:') || originalThumbnailSrc.includes('placeholder')) return;
    if (proxiedThumbnailUrl) return;

    axios.post('https://gotoreconfiles.com/cors_new_server.php', {
      action: 'upload-from-url',
      url: originalThumbnailSrc,
      siteid: siteid
    }, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
    .then(res => {
      if (res.data && typeof res.data === 'string') {
        setProxiedThumbnailUrl(res.data.trim());
      }
    })
    .catch(err => {
      console.warn('Thumbnail proxy failed', err);
    });
  }, [originalThumbnailSrc, proxiedThumbnailUrl, current_dealership, active_vehicle.id]);

  useEffect(() => {
    if (active_vehicle?.id) dispatch(getCatalogParts());
  }, [active_vehicle?.id]);

  const default_labor_rate = toNumber(
    _.find(dealer_settings, s => s.key === 'default_labor_rate' && s.dealership === installation.subdomain)?.value,
    100
  );

  const { groupedItems, refreshVehicle, isDataReady } = useMpiItems(active_vehicle, formInstructions, tab);
  const {
    error,
    setError,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteRowName,
    setDeleteRowName,
    deleteRowId,
    setDeleteRowId,
    flyoutOpen,
    setFlyoutOpen,
    selectedPresets,
    setSelectedPresets,
    selectedCategory,
    setSelectedCategory,
    grandTotal,
    approvedTotal,
    completedTotal,
    pendingTotal,
    partsTotal,
    laborTotal,
    rowTotals,
    setRowTotals,
    confirmingTask,
    setConfirmingTask,
  } = useTotals(groupedItems, formInstructions);

  const vehicle_parts_total = vehiclePartsTotal(active_vehicle);
  const combined_grand_total = (Number(grandTotal || 0) + vehicle_parts_total).toFixed(2);

  const isSimpleMode = formInstructions.sections.some(s => s.layout?.mode === 'simple');

  const legendItems = React.useMemo(() => {
    const map = new Map();
    formInstructions.sections.forEach(section => {
      const sectionHeaders = formInstructions.headers[section.id] || [];
      sectionHeaders.forEach(h => {
        if (['name', 'description'].includes(h.field)) return;
        const parts = h.label.split(':').map(p => p.trim());
        const cleanLabel = parts[0];
        const icon = h.icon || (parts.length > 1 ? parts[1] : null);
        const color = h.true_color || (parts.length > 2 ? parts[2] : '#666');

        if (icon && cleanLabel) {
          const key = `${cleanLabel}-${icon}`;
          if (!map.has(key)) {
            map.set(key, { label: cleanLabel, icon, color });
          }
        }
      });
    });
    return Array.from(map.values());
  }, [formInstructions]);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // PDF Confirmation Modal State
  const [pdfConfirmOpen, setPdfConfirmOpen] = useState(false);
  const [pendingSendSftp, setPendingSendSftp] = useState(true);

  const handleCreatePdfClick = () => {
    if (!formSendSftp) {
      // Form setting disables SFTP → create without sending to iPacket
      handleCreatePdfWithoutSftp();
    } else {
      // Show modal with checkbox
      setPendingSendSftp(true);
      setPdfConfirmOpen(true);
    }
  };

  const handleCreatePdfWithoutSftp = async () => {
    setIsGeneratingPdf(true);
    try {
      const result = await dispatch(createMpiReportPdf(
        active_vehicle.id, 
        customFormId
      ));
      if (result === 'success') {
        await refreshVehicle();
      }
    } catch (err) {
      setError('Failed to create PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const confirmCreatePdf = async () => {
    setPdfConfirmOpen(false);
    setIsGeneratingPdf(true);

    try {
      const shouldSendSftp = pendingSendSftp && formSendSftp;

      let result;
      if (shouldSendSftp) {
        result = await dispatch(createMpiReportPdfAndSend(
          active_vehicle.id, 
          customFormId
        ));
      } else {
        result = await dispatch(createMpiReportPdf(
          active_vehicle.id, 
          customFormId
        ));
      }

      if (result === 'success') {
        await refreshVehicle();
      }
    } catch (err) {
      setError('Failed to create PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Approve All
  const [approveAllConfirmOpen, setApproveAllConfirmOpen] = useState(false);
  const [approveAllSectionId, setApproveAllSectionId] = useState(null);

  const handleApproveAll = (sectionId = null) => {
    setApproveAllSectionId(sectionId);
    setApproveAllConfirmOpen(true);
  };

  const confirmApproveAll = async () => {
    setApproveAllConfirmOpen(false);
    const itemsToApprove = approveAllSectionId 
      ? (groupedItems[approveAllSectionId] || []) 
      : Object.values(groupedItems).flat();

    if (!itemsToApprove.length) return;

    const updatePromises = itemsToApprove.map(item => {
      const data = {
        pass: true,
        pass_by_uid: current_user_profile_id,
        pass_by_date: new Date().toISOString(),
        vehicle_id: active_vehicle.id || vehicle_id,
        form: item.form,
        section: item.section,
      };
      return dispatch(updateMpiWorkItem(item.id, data));
    });

    try {
      await Promise.all(updatePromises);
      await refreshVehicle();
    } catch (err) {
      setError('Failed to pass items');
    }
  };

  const handleBulkActionConfirm = (task) => {
    setConfirmingTask(task);
  };

  const handleBulkActionButtonClick = debounce((task, sectionId, selectedRowIds) => {
    if (!selectedRowIds.length) return;

    const selectedRows = _.filter(groupedItems[sectionId], item =>
      selectedRowIds.includes(parseInt(item.id, 10))
    );
    if (!selectedRows.length) return;

    const bulkAction = task === 'Approve'
      ? (item) => ({
          approved: true,
          declined: false,
          approved_by_uid: current_user_profile_id,
          approved_by_date: new Date().toISOString(),
          declined_by_uid: null,
          declined_by_date: null,
          vehicle_id: active_vehicle.id || vehicle_id,
          form: item.form,
          section: item.section,
        })
      : (item) => ({
          approved: false,
          declined: true,
          approved_by_uid: null,
          approved_by_date: null,
          declined_by_uid: current_user_profile_id,
          declined_by_date: new Date().toISOString(),
          vehicle_id: active_vehicle.id || vehicle_id,
          form: item.form,
          section: item.section,
        });

    const updatePromises = selectedRows.map((item) => {
      const data = bulkAction(item);
      return dispatch(updateMpiWorkItem(item.id, data));
    });

    Promise.all(updatePromises)
      .then(() => {
        setConfirmingTask(null);
        refreshVehicle();
      })
      .catch((error) => {
        setError(`Failed to bulk ${task.toLowerCase()} items`);
      });
  }, 300);

  const handleDelete = (rowName, rowId) => {
    setDeleteRowName(rowName);
    setDeleteRowId(rowId);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteRowId) {
      try {
        await dispatch(deleteMpiWorkItem(deleteRowId));
        await refreshVehicle();
      } catch (error) {
        setError('Failed to delete row');
      }
    }
    setDeleteModalOpen(false);
  };

  const addCustomRow = (sectionId) => {
    const effectiveLaborRate = toNumber(default_labor_rate, 100, 2);
    const labor_hours = toNumber(0, 0, 2);
    const quantity = toNumber(1, 0, 2);
    const parts = toNumber(0, 0, 2);
    const labor_total = labor_hours * effectiveLaborRate;
    const parts_total = parts * quantity;
    const estimate_total = parts_total + labor_total;

    const data = {
      vehicle_id: active_vehicle.id || vehicle_id,
      name: 'custom',
      description: 'New Custom Item',
      layout_type: 'default',
      labor_rate: effectiveLaborRate,
      quantity,
      labor_hours,
      parts,
      parts_total,
      labor_total,
      estimate_total,
      order: (active_vehicle.mpi_work_items?.length || 0) + 1,
      show_mpi: true,
      active: true,
      deleted: false,
      dealership: installation.subdomain,
      category: 'mechanical',
      estimate_type: 'blank_parts_and_labor',
      form: formInstructions.currentFormName,
      section: sectionId,
      ro_status: 'pending',
    };

    dispatch(addMpiWorkItem(data))
      .then(() => refreshVehicle())
      .catch((err) => {
        console.error('Add custom row failed:', err);
        setError(`Failed to add row: ${err.message || 'Unknown error'}`);
      });
  };

  const onDeleteAttachment = (vehicleId, attachmentId) => {
    dispatch(deleteAttachmentWithRefresh(vehicleId, attachmentId))
      .then(refreshVehicle)
      .catch(() => setError('Failed to delete attachment'));
  };

  useEffect(() => {
    if (vehicle_id && (!active_vehicle?.id || !active_vehicle?.mpi_work_items?.length)) {
      dispatch(activeVehicleWithPromise(vehicle_id));
    }
  }, [dispatch, vehicle_id, active_vehicle]);

  const pdfUrl = active_vehicle.mpi_reports?.[0]?.pdf_url || '';

  if (!active_vehicle?.id) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          No vehicle selected
        </Typography>
      </Box>
    );
  }

  if (!formInstructions?.currentFormName || !formInstructions?.sections?.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          No form available for this tab
        </Typography>
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box
        ref={printRef}
        sx={{
          minHeight: '100vh',
          bgcolor: formInstructions.formBackground?.color || '#ffffff',
          backgroundImage: formInstructions.formBackground?.image ? `url(${formInstructions.formBackground.image})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        }}
      >
        <div id="print-inspection" className="container-fluid p-3">
          {error && <div className="alert alert-danger">{error}</div>}

          {isSimpleMode ? (
            <SimpleHeader
              active_vehicle={active_vehicle}
              installation={installation}
              onCreatePdf={handleCreatePdfClick}
              pdfUrl={pdfUrl}
              isGeneratingPdf={isGeneratingPdf}
              proxiedThumbnailUrl={proxiedThumbnailUrl}
              formDisplayName={formInstructions.form_settings?.displayName || 'Multi-Point Inspection Report'}
              legendItems={legendItems}
              onApproveAll={() => handleApproveAll()}
              lastUpdated={lastUpdated}
            />
          ) : (
            <VehicleHeader
              active_vehicle={active_vehicle}
              groupedItems={groupedItems}
              setFlyoutOpen={setFlyoutOpen}
              filename={`work_items_${new Date().toISOString().slice(0, 10).replace(/-/g, '_')}_${active_vehicle.stock}`}
            />
          )}

          {!isSimpleMode && (
            <TotalsDisplay
              formTotaling={formInstructions.formTotaling}
              grandTotal={combined_grand_total}
              approvedTotal={approvedTotal}
              completedTotal={completedTotal}
              pendingTotal={pendingTotal}
              partsTotal={partsTotal}
              laborTotal={laborTotal}
              vehiclePartsTotal={vehicle_parts_total}
            />
          )}

          <VehiclePartsSection vehicle={active_vehicle} tab={tab} />

          {isSimpleMode ? (
            <SectionRenderer
              sections={formInstructions.sections}
              groupedItems={groupedItems}
              headers={formInstructions.headers}
              sectionTotaling={formInstructions.sectionTotaling}
              active_vehicle={active_vehicle}
              vehicle_id={vehicle_id}
              default_labor_rate={default_labor_rate}
              onDelete={handleDelete}
              refreshVehicle={refreshVehicle}
              rowTotals={rowTotals}
              setRowTotals={setRowTotals}
              tableRefs={tableRefs}
              form={formInstructions.currentFormName}
              addCustomRow={addCustomRow}
              handleBulkActionConfirm={handleBulkActionConfirm}
              handleBulkActionButtonClick={handleBulkActionButtonClick}
              work_item_presets={work_item_presets}
              onDeleteAttachment={onDeleteAttachment}
              dispatch={dispatch}
              current_user_profile_id={current_user_profile_id}
              current_dealership={current_dealership}
              setError={setError}
              confirmingTask={confirmingTask}
              isDataReady={isDataReady}
              tab={tab}
              sectionRefs={sectionRefs}
              onApproveAllSection={handleApproveAll}
            />
          ) : (
            <SectionRendererWide
              sections={formInstructions.sections}
              groupedItems={groupedItems}
              headers={formInstructions.headers}
              sectionTotaling={formInstructions.sectionTotaling}
              active_vehicle={active_vehicle}
              vehicle_id={vehicle_id}
              default_labor_rate={default_labor_rate}
              onDelete={handleDelete}
              refreshVehicle={refreshVehicle}
              rowTotals={rowTotals}
              setRowTotals={setRowTotals}
              tableRefs={tableRefs}
              form={formInstructions.currentFormName}
              addCustomRow={addCustomRow}
              handleBulkActionConfirm={handleBulkActionConfirm}
              handleBulkActionButtonClick={handleBulkActionButtonClick}
              work_item_presets={work_item_presets}
              onDeleteAttachment={onDeleteAttachment}
              dispatch={dispatch}
              current_user_profile_id={current_user_profile_id}
              current_dealership={current_dealership}
              setError={setError}
              confirmingTask={confirmingTask}
              isDataReady={isDataReady}
              tab={tab}
              sectionRefs={sectionRefs}
            />
          )}

          <DeleteModal
            open={deleteModalOpen}
            rowName={deleteRowName}
            onConfirm={confirmDelete}
            onClose={() => setDeleteModalOpen(false)}
          />

          <PresetSelector
            open={flyoutOpen}
            onClose={() => setFlyoutOpen(false)}
            selectedPresets={selectedPresets}
            setSelectedPresets={setSelectedPresets}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            sections={formInstructions.sections}
            active_vehicle={active_vehicle}
            vehicle_id={vehicle_id}
            default_labor_rate={default_labor_rate}
            form={formInstructions.currentFormName}
            refreshVehicle={refreshVehicle}
            setError={setError}
          />

          {/* PDF Confirmation Modal */}
          <Dialog open={pdfConfirmOpen} onClose={() => setPdfConfirmOpen(false)}>
            <DialogTitle>Create PDF Report?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Generate the PDF report for this vehicle?
              </DialogContentText>
              
              {formSendSftp && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={pendingSendSftp}
                      onChange={(e) => setPendingSendSftp(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Also send to iPacket"
                  sx={{ mt: 2 }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPdfConfirmOpen(false)}>Cancel</Button>
              <Button 
                onClick={confirmCreatePdf} 
                variant="contained" 
                color="primary"
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? 'Generating...' : 'Create PDF'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Approve All Confirmation Dialog */}
          <Dialog open={approveAllConfirmOpen} onClose={() => setApproveAllConfirmOpen(false)}>
            <DialogTitle>Pass All?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will mark {approveAllSectionId ? 'this section' : 'EVERY item on the form'} as Passed<br /><br />
                Are you sure?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setApproveAllConfirmOpen(false)}>Cancel</Button>
              <Button onClick={confirmApproveAll} variant="contained" color="success">
                Yes, Pass All
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </Box>
    </ErrorBoundary>
  );
};

export default MpiFormBuilder;